// © 2026 KaraokeClubs. All rights reserved.

"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { toZonedTime } from "date-fns-tz";
import { t } from "@/lib/i18n";
import {
  getKaraokeHours,
  getVenueName,
  getPriceLabel,
  DISTRICT_LABELS,
} from "@/lib/venue-helpers";
import type { Venue, Locale, DayHours } from "@/lib/types";
import WeeklyGrid from "./WeeklyGrid";
import { ConfidenceDots } from "./StatusBadge";

const WARSAW_TZ = "Europe/Warsaw";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
type DayKey = (typeof DAY_KEYS)[number];

interface ScheduleViewProps {
  venues: Venue[];
  locale: Locale;
}

function getWarsawNow() {
  return toZonedTime(new Date(), WARSAW_TZ);
}

function getTodayIndex(): number {
  const jsDay = getWarsawNow().getDay(); // 0=Sun
  return jsDay === 0 ? 6 : jsDay - 1; // Convert to 0=Mon
}

function getWeekDates(): Date[] {
  const now = getWarsawNow();
  const todayIdx = getTodayIndex();
  return DAY_KEYS.map((_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() + (i - todayIdx));
    return d;
  });
}

function formatDayLabel(date: Date, locale: Locale): string {
  return date.toLocaleDateString(
    locale === "pl" ? "pl-PL" : locale === "uk" ? "uk-UA" : "en-US",
    { weekday: "short" },
  ).replace(/\.$/, "");
}

function formatStartTime(dh: DayHours): string {
  return dh.open;
}

export default function ScheduleView({ venues, locale }: ScheduleViewProps) {
  const [selectedDay, setSelectedDay] = useState(getTodayIndex);
  const todayIdx = getTodayIndex();
  const weekDates = useMemo(getWeekDates, []);

  const dayKey = DAY_KEYS[selectedDay];
  const isToday = selectedDay === todayIdx;

  // Get venues with karaoke on selected day, sorted by start time
  const dayVenues = useMemo(() => {
    const result: { venue: Venue; hours: DayHours; karaokeHours: NonNullable<ReturnType<typeof getKaraokeHours>> }[] = [];

    for (const venue of venues) {
      const kh = getKaraokeHours(venue);
      if (!kh) continue;
      const dh = kh[dayKey];
      if (!dh) continue;
      result.push({ venue, hours: dh, karaokeHours: kh });
    }

    // Sort by start time
    result.sort((a, b) => {
      const aMin = parseTime(a.hours.open);
      const bMin = parseTime(b.hours.open);
      return aMin - bMin;
    });

    return result;
  }, [venues, dayKey]);

  // Group by time slots
  const timeGroups = useMemo(() => {
    const groups: Map<string, typeof dayVenues> = new Map();
    for (const item of dayVenues) {
      const slot = item.hours.open;
      if (!groups.has(slot)) groups.set(slot, []);
      groups.get(slot)!.push(item);
    }
    return Array.from(groups.entries());
  }, [dayVenues]);

  return (
    <div className="flex flex-col h-full">
      {/* Day picker — horizontal scroll on mobile, full row on desktop */}
      <div className="shrink-0 px-1 pt-1 pb-3">
        <div className="flex gap-1">
          {DAY_KEYS.map((_, i) => {
            const date = weekDates[i];
            const isSelected = i === selectedDay;
            const isTodayPill = i === todayIdx;
            const dayLabel = formatDayLabel(date, locale);
            const dateNum = date.getDate();

            // Count venues with karaoke this day
            const count = venues.filter((v) => {
              const kh = getKaraokeHours(v);
              return kh && kh[DAY_KEYS[i]] !== null;
            }).length;

            return (
              <button
                key={i}
                onClick={() => setSelectedDay(i)}
                className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 rounded-xl transition-all touch-manipulation select-none ${
                  isSelected
                    ? isTodayPill
                      ? "bg-emerald-500/15 border border-emerald-500/30 shadow-lg shadow-emerald-900/10"
                      : "bg-violet-500/15 border border-violet-500/25 shadow-lg shadow-violet-900/10"
                    : "bg-white/[0.02] border border-transparent hover:bg-white/[0.04] active:scale-95"
                }`}
              >
                <span className={`text-[10px] font-bold uppercase tracking-wide ${
                  isSelected
                    ? isTodayPill ? "text-emerald-400" : "text-violet-400"
                    : "text-zinc-500"
                }`}>
                  {dayLabel}
                </span>
                <span className={`text-sm font-bold leading-none ${
                  isSelected
                    ? isTodayPill ? "text-emerald-300" : "text-violet-300"
                    : "text-zinc-300"
                }`}>
                  {dateNum}
                </span>
                {count > 0 && (
                  <span className={`text-[9px] font-semibold ${
                    isSelected
                      ? isTodayPill ? "text-emerald-400/70" : "text-violet-400/70"
                      : "text-zinc-600"
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Schedule list */}
      <div className="flex-1 overflow-y-auto pb-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedDay}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.15 }}
          >
            {dayVenues.length === 0 ? (
              <div className="text-center py-16 px-4">
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-zinc-800/50 flex items-center justify-center text-2xl">
                  {"\u{1F634}"}
                </div>
                <p className="text-zinc-400 text-sm font-medium mb-1">
                  {t(locale, "no_karaoke_day")}
                </p>
                <p className="text-zinc-600 text-xs">
                  {t(locale, "try_another_day")}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {timeGroups.map(([time, items]) => (
                  <div key={time}>
                    {/* Time header */}
                    <div className="flex items-center gap-2.5 px-2 py-2">
                      <div className={`w-[52px] shrink-0 text-right tabular-nums text-sm font-bold ${
                        isToday ? "text-emerald-400" : "text-violet-400"
                      }`}>
                        {time}
                      </div>
                      <div className={`flex-1 h-px ${
                        isToday ? "bg-emerald-500/15" : "bg-violet-500/10"
                      }`} />
                    </div>

                    {/* Venues at this time */}
                    <div className="space-y-1.5 pl-[62px] pr-1">
                      {items.map(({ venue, hours }) => {
                        const name = getVenueName(venue, locale);
                        return (
                          <Link
                            key={venue.id}
                            href={`/venue/${venue.slug}`}
                            className="block group"
                          >
                            <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.04]
                                            group-hover:bg-white/[0.05] group-hover:border-white/[0.1] transition-all active:scale-[0.99]">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <h3 className="font-bold text-[14px] truncate group-hover:text-violet-300 transition-colors">
                                    {name}
                                  </h3>
                                  {venue.is_dedicated_karaoke && (
                                    <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-500/15 text-violet-400 border border-violet-500/20">
                                      {t(locale, "dedicated")}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-[12px] text-zinc-500">
                                  <span>{DISTRICT_LABELS[venue.district] ?? venue.district}</span>
                                  <span className="text-zinc-700">&middot;</span>
                                  <span className="tabular-nums">{hours.open} – {hours.close}</span>
                                  {venue.price_range && (
                                    <>
                                      <span className="text-zinc-700">&middot;</span>
                                      <span className="text-amber-400/70">{getPriceLabel(venue.price_range)}</span>
                                    </>
                                  )}
                                  <ConfidenceDots confidence={venue.karaoke_schedule_confidence} />
                                </div>
                              </div>
                              <svg className="w-4 h-4 text-zinc-600 shrink-0 group-hover:text-zinc-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function parseTime(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}
